import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Sparkles, LogOut, LayoutDashboard, Menu, X, Mail, User, KeyRound } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import NotificationBell from "./NotificationBell";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  const links = [
    { to: "/events", label: "Événements" },
    { to: "/venues", label: "Lieux" },
    { to: "/providers", label: "Prestataires" },
  ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-cream/85 border-b border-ink/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          <Link to="/" className="flex items-baseline gap-1" data-testid="brand-logo">
            <span className="font-heading font-light text-2xl tracking-editorial text-ink">
              Event<span className="font-display italic text-accent">Market</span>
            </span>
            <span className="text-label text-ink/40 hidden sm:inline">Pro</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="text-[13px] font-medium text-ink/70 hover:text-ink transition-colors tracking-wide"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-cream-100">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.picture || ""} />
                      <AvatarFallback className="bg-primary text-white text-xs">
                        {user.name?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                    <p className="text-xs text-trust mt-1 capitalize">{user.role}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="menu-dashboard">
                    <LayoutDashboard className="h-4 w-4 mr-2" /> Tableau de bord
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/settings/profile")} data-testid="menu-profile">
                    <User className="h-4 w-4 mr-2" /> Modifier mon profil
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/settings/email")} data-testid="menu-email-prefs">
                    <Mail className="h-4 w-4 mr-2" /> Préférences email
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/settings/password")} data-testid="menu-change-password">
                    <KeyRound className="h-4 w-4 mr-2" /> Changer mon mot de passe
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="h-4 w-4 mr-2" /> Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login">
                  <Button variant="ghost" size="sm" className="text-ink/70 hover:text-ink hover:bg-transparent">Se connecter</Button>
                </Link>
                <Link to="/register" data-testid="nav-register">
                  <Button size="sm" className="bg-ink hover:bg-ink/85 text-cream rounded-none px-5">
                    S'inscrire
                  </Button>
                </Link>
              </>
            )}
          </div>

          <button
            onClick={() => setOpen(!open)}
            className="md:hidden p-2"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-ink/10 py-3 space-y-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="block px-3 py-2 rounded-md hover:bg-cream-100"
              >
                {l.label}
              </Link>
            ))}
            <div className="pt-2 border-t border-ink/10 mt-2">
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="block px-3 py-2 rounded-md hover:bg-cream-100">
                    Tableau de bord
                  </Link>
                  <button onClick={handleLogout} className="w-full text-left block px-3 py-2 rounded-md hover:bg-cream-100">
                    Déconnexion
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="block px-3 py-2 rounded-md hover:bg-cream-100">
                    Se connecter
                  </Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="block px-3 py-2 bg-ink text-cream">
                    S'inscrire
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
